import get from "lodash.get";

/**
 * Procedure Construct non-payables - the money math and the row predicates.
 *
 * Everything the Procedure Construct editor knows about a tariff row lives here so the
 * savings split is implemented exactly once. The editor prices a decision, writes it onto the
 * row, and the written row is what reaches the tariff list and the save payload.
 *
 * Three questions sort a row between the editor's three lists, asked strictly in this order by
 * `pcRouteOf` - the first to answer decides:
 *
 *   1. irdai_payable             false -> IRDAI: off the claim already, nothing to decide
 *   2. the construct's ruling    found -> Bill Line Item: `constructFlagsLineItem`
 *   3. procedure_construct_payable_text
 *                                flagged -> Bill Line Item, waiting to be disallowed or adjusted
 *                                otherwise -> Disallowed, the pool a line item is disallowed from
 *
 * `procedure_construct_payable` is the boolean written beside that text and always agrees with
 * it. The text is what is read, because it is the one that can tell a partial from a whole.
 *
 * The construct carries its ruling in `construct_object`, in two fields that say overlapping
 * things - a claim may arrive with either or both, so both are read and either one is enough:
 *
 *   status: PAYABLE       allowed: true          payable in full
 *   status: CAPPED        allowed: true          payable down to allowed_quantity
 *   status: DISALLOWED    allowed: false         not payable at all
 *
 * `constructFlagsLineItem` is the one place those are put together, and `constructPayableQuantity`
 * turns the pair into the payable units the Payable Qty box opens on. Nothing else reads either
 * field, so a third way of saying it - or a claim that only ever sends one of them - lands in
 * one function rather than in every caller.
 *
 * Field contract on a tariff row:
 *   read only   - unit, unit_amount, tariff_rate, total_bill_amount, irdai_payable
 *   the finding - construct_remarks, allowed_quantity_by_construct (both top level; the Java
 *                 layer in front of this UI renames the latter to allowed_units_by_proc_construct),
 *                 and construct_object.{allowed, status, allowed_quantity, construct_remarks}
 *   written     - allowed_units_by_proc_construct, allowed_quantity_by_construct (only where the
 *                 row already carried one), procedure_construct_payable,
 *                 procedure_construct_payable_text, remarks, tariff_amount, admissible_amount,
 *                 savings, deduction, insurerAmount, and - on a pull-back only -
 *                 construct_object.{allowed, status}
 *
 * `remarks` is the row's own note and never leaves the commit empty: a typed reason stands, and
 * a row nobody wrote one for carries the construct's own `construct_remarks` instead. See
 * `resolvedRemarksOf`. The construct's remarks are read but never written - they are its words.
 *
 * The bill is never trimmed: unit / unit_amount / tariff_rate keep the figures the claim
 * delivered, and how far a decision reaches is carried by allowed_units_by_proc_construct
 * (payable units, which is what the Payable Qty column shows and edits), the shape the save
 * payload already speaks. `construct_object.charged_quantity` is deliberately not read - `unit`
 * is the bill's own figure and the only one a decision may be measured against.
 */

const PC_PAYABLE_TEXT = "Payable by Procedure Construct";
const PC_PARTIALLY_PAYABLE_TEXT = "Partially Payable by Procedure Construct";
const PC_NON_PAYABLE_TEXT = "Non-Payable by Procedure Construct";

const PC_FLAGGED_TEXTS = [PC_NON_PAYABLE_TEXT, PC_PARTIALLY_PAYABLE_TEXT];

// A rupee either way is not a saving worth offering, and it keeps float dust out of the list.
export const MONEY_EPSILON = 0.005;

export const rowKey = (row) => get(row, ['id'], get(row, ['row_id']));

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const isStated = (value) => value !== null && value !== undefined && value !== '';

/**
 * A figure the claim actually stated, or null where it stated none.
 *
 * Zero and negative figures are stated figures. Only a field the claim never filled in - null,
 * undefined, blank, or something that will not parse as a number - answers null here, which is
 * what separates "the bill says nothing" from "the bill says nothing is owed".
 */
const statedNumber = (value) => {
    if (!isStated(value)) {
        return null;
    }
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const roundMoney = (value) => Math.round(toNumber(value) * 100) / 100;

export const formatRupees = (value) =>
    `₹ ${roundMoney(value).toLocaleString('en-IN', {maximumFractionDigits: 2})}`;

export const formatPercent = (value) => `${(Math.round(toNumber(value) * 10) / 10).toFixed(1)}`;

/** What the bill charged. */
export const chargedQuantity = (row) => Math.max(toNumber(get(row, ['unit'])), 0);

/**
 * What the construct said about the line item, in its own words.
 *
 * Two places carry it and either one on its own is enough. The flat field is what the API
 * returns today; `construct_object.construct_remarks` is where the construct itself writes the
 * sentence, and a great many claims arrive carrying only that one - the ruling and its reason
 * travelling together:
 *
 *   {"allowed": false, "status": "DISALLOWED", "construct_remarks":
 *    "For Viral Fever this item is not required during hospitalization.", ...}
 *
 * The flat field is asked first and the nested one answers whenever it is blank. Blank and not
 * merely absent, because a row can arrive with `construct_remarks: ""` or `null` beside a
 * construct that did write a sentence, and a default that only fires on `undefined` would read
 * the empty one and never look inside the ruling that explains it.
 */
export const constructRemarksOf = (row) => {
    const flat = String(get(row, ['construct_remarks'], '') || '').trim();
    return flat || String(get(row, ['construct_object', 'construct_remarks'], '') || '').trim();
};

/**
 * The note carried on the row itself - the same field the tariff grid's Remarks column edits.
 * It is a different thing from the construct's own remarks, which are the construct's to write
 * and nobody else's.
 */
export const remarksOf = (row) => String(get(row, ['remarks'], '') || '').trim();

/**
 * The remark a row leaves this panel carrying, and it is never blank.
 *
 * A decision that reaches the save payload has to say why it was taken, and there are only ever
 * two answers to that - so they are asked in the order of who spoke last:
 *
 *   the row's own `remarks`   somebody typed a reason for this line item. A person overruling
 *                             the construct, or agreeing with it in their own words, is the last
 *                             word on the row and is never written over.
 *   the construct's own       nobody typed anything, so the reason is the one the construct gave
 *                             when it ruled - `construct_object.construct_remarks`, the sentence
 *                             that came with the ruling being committed.
 *
 * A row with neither resolves to '', which is the one case there is genuinely nothing to say:
 * the construct ruled without a reason and nobody added one. Nothing invents a sentence here.
 */
export const resolvedRemarksOf = (row) => remarksOf(row) || constructRemarksOf(row);

/**
 * Write that note. Nothing else on the row moves - a remark is not a decision.
 *
 * Blank text does not blank the field: it falls back to the construct's own words, on the same
 * rule `resolvedRemarksOf` reads by. The dialog already refuses an empty remark, so this is the
 * floor under every other caller rather than a path a person can take - a row must not reach the
 * save payload explaining itself with nothing.
 */
export const stageRemarks = (row, text) => {
    const written = String(text === null || text === undefined ? '' : text);
    return {...row, remarks: written.trim() ? written : constructRemarksOf(row)};
};

/** The verdict the row carries, in the words both tariff grids render. */
export const pcVerdictTextOf = (row) =>
    String(get(row, ['procedure_construct_payable_text'], '') || '').trim();

export const unitAmountOf = (row) => toNumber(get(row, ['unit_amount']));

export const tariffRateOf = (row) => toNumber(get(row, ['tariff_rate']));

export const savingOf = (row) => toNumber(get(row, ['savings']));

/**
 * What the bill charged for the whole line item.
 *
 * Whatever figure the claim stated is the figure, zero and negative ones included. A credit
 * note, a reversal, a line item written off - those are real figures on a real bill, the tariff
 * grid prints them as they stand, and this screen printing a different number for the same line
 * item was the two views disagreeing about the same claim.
 *
 * The fallback is for an unstated field only, where `unit x unit_amount` is the only bill
 * there is. It is deliberately not reached for a stated zero: reading that as "unstated" is
 * what used to substitute a number the claim never carried.
 */
export const billAmountOf = (row) => {
    const stated = statedNumber(get(row, ['total_bill_amount']));
    return stated === null ? chargedQuantity(row) * unitAmountOf(row) : stated;
};

export const clampQuantity = (quantity, charged) => {
    const value = Math.max(toNumber(quantity), 0);
    // No decision can reach past what the bill carried.
    return charged > 0 ? Math.min(value, charged) : value;
};

/**
 * Units the construct allows, or null when the claim never stated a figure. The backend's own
 * name is asked first; `allowed_units_by_proc_construct` is the same figure under the name this
 * UI receives it by, and is also where a decision taken here writes its reach.
 */
const constructAllowedQuantity = (row) => {
    const stated = get(row, ['allowed_quantity_by_construct']);
    if (isStated(stated)) {
        return toNumber(stated);
    }
    const renamed = get(row, ['allowed_units_by_proc_construct']);
    return isStated(renamed) ? toNumber(renamed) : null;
};

/** Units the construct disallows, from the construct's own figures only. */
export const constructNonPayableQuantity = (row) => {
    const allowed = constructAllowedQuantity(row);
    if (allowed === null) {
        return null;
    }
    const charged = chargedQuantity(row);
    const nonPayable = charged - allowed;
    return nonPayable > 0 ? clampQuantity(nonPayable, charged) : null;
};

/**
 * The construct's own three answers, in its own words. `status` is the field that says which of
 * them a line item got; `allowed` is the older, coarser way of saying the same thing.
 */
export const CONSTRUCT_STATUS = {
    PAYABLE: 'PAYABLE',
    CAPPED: 'CAPPED',
    DISALLOWED: 'DISALLOWED',
};

/** What the construct itself said about the line item: allowed, not allowed, or nothing. */
export const constructAllowsLineItem = (row) => get(row, ['construct_object', 'allowed']);

/** ...and which of its three answers it gave, upper-cased and trimmed, or '' where it gave none. */
export const constructStatusOf = (row) =>
    String(get(row, ['construct_object', 'status'], '') || '').trim().toUpperCase();

/**
 * Did the construct rule against this line item at all?
 *
 * Two fields carry that ruling and either one on its own is enough, which is the whole point of
 * asking here rather than in each caller: a claim may arrive with only `allowed`, only `status`,
 * or both, and every reader has to reach the same answer from whichever it was given.
 *
 *   allowed: false        the whole line item is out. Asked first, and enough on its own -
 *                         a veto stands whatever the status beside it reads.
 *   status DISALLOWED     the same answer in the newer field.
 *   status CAPPED         allowed, but only so far. A finding in its own right: the line item
 *                         needs a person to see it even though `allowed` reads true - which is
 *                         exactly the shape `{allowed: true, allowed_quantity: 1, status:
 *                         "CAPPED"}` arrives in. A cap at or above the charged units cuts
 *                         nothing, so that one is not a finding.
 *   status PAYABLE        no finding. Asked before `allowed_quantity` is read at all, which is
 *                         what lets a pull-back stick: `clearConstructFinding` writes this
 *                         status, and the construct's original cap is then left standing as a
 *                         record without being read as a ruling any more.
 *   no status             the older payload shape. A cap below the charged units is the finding,
 *                         read exactly as it always was - a `0` beside `allowed: true` is the
 *                         backend's unstated primitive and means nothing here.
 */
export const constructFlagsLineItem = (row) => {
    if (!get(row, ['construct_object'])) {
        return false;
    }
    if (constructAllowsLineItem(row) === false) {
        return true;
    }
    const status = constructStatusOf(row);
    if (status === CONSTRUCT_STATUS.DISALLOWED) {
        return true;
    }
    if (status === CONSTRUCT_STATUS.PAYABLE) {
        return false;
    }
    const charged = chargedQuantity(row);
    const cap = toNumber(get(row, ['construct_object', 'allowed_quantity']));
    if (status === CONSTRUCT_STATUS.CAPPED) {
        return !(charged > 0) || cap < charged;
    }
    return cap > 0 && charged > 0 && cap < charged;
};

/**
 * What the construct allows, in payable units - the figure the Payable Qty box opens on. Null
 * where the construct ruled on nothing, so that a row it never touched is left to the figures
 * the row itself carries.
 *
 *   veto, or DISALLOWED   nought payable
 *   CAPPED, or a bare cap the cap, clamped to what the bill charged. A CAPPED status with no
 *                         figure beside it caps at nought: the construct says this line item is
 *                         limited without saying to what, and reading that as "pay all of it"
 *                         would be the one answer it certainly did not give. The row lands in
 *                         the Bill Line Item table either way, where a person sets the figure.
 *
 * PAYABLE never reaches here - `constructFlagsLineItem` has already answered false for it - so
 * this is only ever asked of a line item the construct ruled against.
 */
export const constructPayableQuantity = (row) => {
    if (!constructFlagsLineItem(row)) {
        return null;
    }
    if (constructAllowsLineItem(row) === false
        || constructStatusOf(row) === CONSTRUCT_STATUS.DISALLOWED) {
        return 0;
    }
    return clampQuantity(get(row, ['construct_object', 'allowed_quantity']), chargedQuantity(row));
};

/**
 * ...and the same ruling as a cut, which is what the money side of this file wants. Null where
 * nothing is cut, so a claim whose rows the construct never ruled on deducts nothing.
 *
 * Deliberately separate from `constructNonPayableQuantity` above. That one reads
 * `allowed_units_by_proc_construct`, which is the row's *current* allowed units - the field a
 * staged quantity is written to - and which the backend sends as a primitive `0` whether the
 * construct disallowed the whole line item or said nothing about it at all. Only
 * `construct_object` can tell those two apart, and only through the fields above.
 */
export const constructObjectNonPayableQuantity = (row) => {
    const payable = constructPayableQuantity(row);
    if (payable === null) {
        return null;
    }
    const charged = chargedQuantity(row);
    const nonPayable = charged - payable;
    return nonPayable > 0 ? clampQuantity(nonPayable, charged) : null;
};

/**
 * Units already disallowed on a decided row - the figure the decision wrote, not one being
 * staged. A row that came back non-payable without one had its whole line item taken.
 */
export const appliedNonPayableQuantity = (row) => {
    const charged = chargedQuantity(row);
    const byConstruct = constructNonPayableQuantity(row);
    return byConstruct === null ? charged : byConstruct;
};

/**
 * The verdict a row carries, decided by how much of it is left allowed. The boolean and the
 * text are one fact written twice - the boolean is what this UI sorts rows by, the text is what
 * both tariff grids render - so this is the only place the pairing is settled, and it is the
 * same three-way rule the construct itself applies:
 *
 *   allowed 0             -> false, "Non-Payable by Procedure Construct"
 *   0 < allowed < charged -> true,  "Partially Payable by Procedure Construct"
 *   allowed >= charged    -> true,  "Payable by Procedure Construct"
 *
 * Note the middle case: a partially payable line item is still payable, so its flag stays true
 * and it stays in the Bill Line Item table. Only one disallowed outright moves to Disallowed.
 */
export const pcVerdictFor = (charged, allowedUnits) => {
    if (!(allowedUnits > 0)) {
        return {payable: false, text: PC_NON_PAYABLE_TEXT};
    }
    if (charged > 0 && allowedUnits < charged) {
        return {payable: true, text: PC_PARTIALLY_PAYABLE_TEXT};
    }
    return {payable: true, text: PC_PAYABLE_TEXT};
};

/** The decision in force on the row, read from its verdict: none of it is payable. */
export const isPcNonPayable = (row) => pcVerdictTextOf(row) === PC_NON_PAYABLE_TEXT;

/**
 * Is this line item fully payable, by its own verdict?
 *
 * The verdict text is the only thing asked - never the boolean beside it. The two are written
 * together and always agree, but the text is what both tariff grids render and it is the one
 * that can tell a partial from a whole:
 *
 *   "Payable by Procedure Construct"            true   - all of it is payable
 *   "Partially Payable by Procedure Construct"  false  - part of it is cut
 *   "Non-Payable by Procedure Construct"        false  - all of it is cut
 *
 * Only those two flagged verdicts say something is cut. Any other text - "Procedure not found"
 * and its like, which other backend paths emit alongside a payable boolean - claims no
 * shortfall, so the line item is taken as payable.
 */
export const isPcFullyPayable = (row) => !PC_FLAGGED_TEXTS.includes(pcVerdictTextOf(row));

/**
 * How far a decision on this row reaches once it is committed, in non-payable units - the
 * complement of what the Charged Qty / Payable Qty column shows, and the figure Save & Adjudicate
 * prices. The two have to agree: charged less this is the number in the box.
 *
 * Two things are asked, and the verdict is asked first:
 *
 *   1. procedure_construct_payable_text  is it still "Payable by Procedure Construct"? Only then
 *                                        is anything below consulted. Any other text means
 *                                        somebody has already said how far this row goes - a
 *                                        quantity settled in the column writes "Partially
 *                                        Payable" or "Non-Payable" as it writes the units - and
 *                                        a hand-set figure is never overruled here.
 *   2. the construct's own ruling        `constructPayableQuantity`, which is where `allowed`
 *                                        and `status` are put together. A veto or DISALLOWED
 *                                        commits the whole line item; CAPPED commits down to its
 *                                        cap, so `{allowed: true, allowed_quantity: 1, status:
 *                                        "CAPPED"}` on four charged units commits three - and
 *                                        the box reads 4 / 1 before the button is pressed, which
 *                                        is the figure the construct actually gave.
 *
 * Without the first gate this would discard typed quantities. Without the second, a row the
 * construct ruled against while its verdict still read "Payable" would commit on whatever the
 * top-level fields happened to carry - which for a great many rows is the backend's primitive
 * `0`, and so the whole line item, cap and all.
 *
 * Then one correction, and only one. A verdict of "Partially Payable" with nothing left payable
 * beside it is not a decision anybody took - it is that same primitive `0` sitting under a text
 * that contradicts it. Where the construct put a figure to the cap, that figure is the only
 * coherent reading of the row, so it is used. Every other row falls through to
 * `appliedNonPayableQuantity`, the figure the row itself carries.
 */
export const committedNonPayableQuantity = (row) => {
    const charged = chargedQuantity(row);
    const verdict = pcVerdictTextOf(row);
    const byConstruct = constructPayableQuantity(row);
    const fromConstruct = () => Math.max(charged - byConstruct, 0);

    if (verdict === PC_PAYABLE_TEXT && byConstruct !== null) {
        return fromConstruct();
    }

    const applied = appliedNonPayableQuantity(row);
    if (verdict === PC_PARTIALLY_PAYABLE_TEXT && byConstruct > 0 && charged > 0 && applied >= charged) {
        return fromConstruct();
    }
    return applied;
};

export const isIrdaiNonPayable = (row) => get(row, ['irdai_payable']) === false;

/** Which list a row belongs in. One row, one answer, and every reader asks the same question. */
export const PC_ROUTE = {
    IRDAI: 'irdai',
    BILL_LINE_ITEM: 'billLineItem',
    DISALLOWED: 'disallowed',
};

/**
 * Which of the editor's three lists a row belongs in, decided by three fields asked strictly in
 * this order. The first one to answer decides, and the ones below it are not consulted:
 *
 *   1. irdai_payable            false -> IRDAI. Not payable under IRDAI, so it is off the claim
 *                               before the Procedure Construct is consulted at all. Nothing
 *                               below can put it back: a construct ruling on a line IRDAI does
 *                               not pay has nothing left to rule on, and a verdict on it is a
 *                               verdict on money already gone. This is why it is asked first.
 *   2. the construct's ruling   found -> Bill Line Item. `constructFlagsLineItem`, which puts
 *                               `allowed` and `status` together and answers true to any of them:
 *                               a veto, a DISALLOWED status, or a CAPPED one whose cap bites.
 *                               The finding stands whatever text the row carries beside it - a
 *                               row can arrive ruled against while its verdict still reads
 *                               "Payable by Procedure Construct", and a CAPPED row routinely
 *                               does.
 *
 *                               A pull-back has to survive this, and that is why it is asked as
 *                               a ruling rather than as a cap: `clearConstructFinding` writes
 *                               `status: PAYABLE` beside `allowed: true`, and the status is read
 *                               before `allowed_quantity` is looked at - so the construct's
 *                               original cap is left standing as a record without bouncing the
 *                               row straight back into this list and making the pull-back look
 *                               like it did nothing.
 *   3. procedure_construct_payable_text
 *                               "Non-Payable" or "Partially Payable" -> Bill Line Item: the
 *                               claim is not paying it in full. Anything else -> Disallowed, the
 *                               pool a line item is disallowed *from*. The text and not the
 *                               boolean beside it, because it is the one that can tell a partial
 *                               from a whole.
 *
 * One function with three answers rather than three predicates that have to agree: a row lands
 * in exactly one list, so the three partition the tariff sheet and no line item can be shown
 * twice, counted twice, or lost between them.
 */
export const pcRouteOf = (row) => {
    if (isIrdaiNonPayable(row)) {
        return PC_ROUTE.IRDAI;
    }
    if (constructFlagsLineItem(row)) {
        return PC_ROUTE.BILL_LINE_ITEM;
    }
    return isPcFullyPayable(row) ? PC_ROUTE.DISALLOWED : PC_ROUTE.BILL_LINE_ITEM;
};

/**
 * The three lists, each as its own question. `isPcDisallowed` is no longer the negation of
 * `isPcBillLineItem` - there is a third answer now, and a row IRDAI does not pay is in neither
 * of these two.
 */
export const isPcBillLineItem = (row) => pcRouteOf(row) === PC_ROUTE.BILL_LINE_ITEM;

export const isPcDisallowed = (row) => pcRouteOf(row) === PC_ROUTE.DISALLOWED;

export const isIrdaiListItem = (row) => pcRouteOf(row) === PC_ROUTE.IRDAI;

/**
 * Did the Procedure Construct flag this line item at all? Either it has already been
 * disallowed, or its verdict names a shortfall, or it allows fewer units than were charged.
 */
export const isConstructFlagged = (row) =>
    isPcNonPayable(row) ||
    PC_FLAGGED_TEXTS.includes(pcVerdictTextOf(row)) ||
    constructNonPayableQuantity(row) !== null ||
    constructFlagsLineItem(row);

/**
 * What the insurer pays: the units left allowed, at what the bill charged for them.
 *
 *   "Payable by Procedure Construct"            unit x unit_amount
 *   "Partially Payable by Procedure Construct"  allowed_units_by_proc_construct x unit_amount
 *   "Non-Payable by Procedure Construct"        0
 *
 * The three collapse to one line, because a payable line item allows every charged unit and a
 * non-payable one allows none. Note this is priced at `unit_amount`, not at the tariff rate -
 * `tariff_amount` is the tariff's answer and this is the bill's.
 */
export const insurerAmountFor = (row, allowedUnits) =>
    roundMoney(clampQuantity(allowedUnits, chargedQuantity(row)) * unitAmountOf(row));

/**
 * Price a decision taken on `pcUnit` units of `row`.
 *
 *   pcSaving     = pcUnit x unitAmount                      disallowed units save their whole bill
 *   tariffAmount = (charged - pcUnit) x min(tariffRate, unitAmount)   the rest stay at tariff
 *   savings      = billAmount - tariffAmount                the two put together
 */
export const pcNonPayableAmounts = (row, pcUnit) => {
    const charged = chargedQuantity(row);
    const unitAmount = unitAmountOf(row);
    const tariffRate = tariffRateOf(row);
    const billAmount = billAmountOf(row);
    const units = clampQuantity(pcUnit, charged);

    // No usable unit breakup - the line item is decided on whole.
    if (!(charged > 0) || !(unitAmount > 0)) {
        return {
            pcUnit: charged,
            allowedUnits: 0,
            pcSaving: roundMoney(billAmount),
            billAmount: roundMoney(billAmount),
            tariffAmount: 0,
            insurerAmount: 0,
            savings: roundMoney(billAmount),
        };
    }

    // No tariff rate - the payable units stay admissible in full.
    const rate = tariffRate > 0 ? Math.min(tariffRate, unitAmount) : unitAmount;
    const allowedUnits = Math.max(charged - units, 0);
    const tariffAmount = allowedUnits * rate;

    return {
        pcUnit: units,
        allowedUnits,
        pcSaving: roundMoney(units * unitAmount),
        billAmount: roundMoney(billAmount),
        tariffAmount: roundMoney(tariffAmount),
        insurerAmount: insurerAmountFor(row, allowedUnits),
        savings: roundMoney(billAmount - tariffAmount),
    };
};

/**
 * What disallowing `pcUnit` units would add to the claim on top of what the row saves as it
 * stands. Read against the row's own current saving rather than a remembered one, so the
 * figure stays honest however many times a row is disallowed and pulled back.
 */
export const addedSavingIfMarked = (row, pcUnit) =>
    roundMoney(pcNonPayableAmounts(row, pcUnit).savings - savingOf(row));

/**
 * Every field a decision overwrites. Snapshotting the set on first sight is what lets a row's
 * money be put back the way it was found. `procedure_construct_payable_text` is snapshotted
 * with the rest but deliberately not restored: it is written from the allowed units instead, so
 * the verdict a row carries always agrees with the figures beside it.
 */
const DECIDED_FIELDS = [
    'allowed_units_by_proc_construct',
    // The same figure under the backend's own name, snapshotted because `stagePayableQuantity`
    // writes it wherever a row carries one - and `constructAllowedQuantity` asks for it first, so
    // a restore that put back only the field beside it would read back the figure it just undid.
    // A row that never carried one snapshots undefined and is restored to undefined, which is
    // what `isStated` already treats as no figure at all.
    'allowed_quantity_by_construct',
    'procedure_construct_payable',
    'procedure_construct_payable_text',
    'tariff_amount',
    'admissible_amount',
    'savings',
    'deduction',
    'insurerAmount',
];

/** What the row arrived carrying, taken once on first sight. */
export const baselineOf = (row) => ({
    verdictText: pcVerdictTextOf(row),
    arrivalSaving: savingOf(row),
    money: DECIDED_FIELDS.reduce((snapshot, field) => {
        snapshot[field] = get(row, [field]);
        return snapshot;
    }, {}),
});

/** Was this row found payable, so that putting it back is an exact restore? */
export const arrivedPayable = (baseline) =>
    Boolean(baseline) && !PC_FLAGGED_TEXTS.includes(get(baseline, ['verdictText'], ''));

/**
 * ...and was it found already disallowed outright - "Non-Payable by Procedure Construct" on
 * first sight?
 *
 * Then its money came with it: whoever decided it also saved it, and the claim has been carrying
 * that saving since before this panel was opened. A row like that is history, not a decision
 * waiting to be taken, and the figures the panel offers to add to the claim must not count it
 * twice. Asked of the arrival snapshot and never of the row as it now stands, because the row's
 * verdict moves the moment somebody settles a quantity on it - at which point it is a decision
 * again, and it counts.
 */
export const arrivedNonPayable = (baseline) =>
    Boolean(baseline) && get(baseline, ['verdictText'], '') === PC_NON_PAYABLE_TEXT;

/**
 * Record how far a pending decision reaches, without taking it.
 *
 * The figure is payable units - what the Charged Qty / Payable Qty column shows and edits. 16
 * charged with 8 settled is 8 payable and 8 not; 16 is all of it payable; 0 is none of it. That
 * is already exactly what `allowed_units_by_proc_construct` means, so the column writes the
 * field straight through with nothing inverted on the way in or out, and reading the field back
 * is how the column repopulates. The tariff grid, the save payload and the construct itself all
 * speak that same field, so the figure lives on the row rather than beside it.
 *
 * `allowed_quantity_by_construct` is written alongside it, but only where the row already
 * carries one. That is the name `constructAllowedQuantity` asks for first, so a row arriving
 * with it would otherwise read back the construct's original cap however many units were
 * settled here - the column would appear to refuse what was typed into it. Rows without one are
 * left without one: this writes what a row already has, it does not invent a backend field.
 *
 * The verdict goes with the units, because the flag and the text are read off them and must
 * never lag behind: leave some payable and the row is Partially Payable and stays where it is;
 * leave none and it is Non-Payable; leave all of them and it is Payable, which is no longer a
 * decision at all - see `settleQuantity`, which sends that case down the pull-back path instead
 * of here, because a veto standing on the row would otherwise keep it in a table it has just
 * been let out of.
 *
 * The money is deliberately left alone. Nothing has been decided until a decision lands, and
 * pricing the row here would put savings on the claim that nobody has agreed to yet.
 */
export const stagePayableQuantity = (row, payableUnits) => {
    const charged = chargedQuantity(row);
    const allowedUnits = clampQuantity(payableUnits, charged);
    const verdict = pcVerdictFor(charged, allowedUnits);
    const staged = {
        ...row,
        allowed_units_by_proc_construct: allowedUnits,
        procedure_construct_payable: verdict.payable,
        procedure_construct_payable_text: verdict.text,
        // What the insurer pays follows the allowed units directly, so it moves with them.
        // The savings do not: nothing is decided yet, and pricing them here would put money on
        // the claim's total that nobody has agreed to.
        insurerAmount: insurerAmountFor(row, allowedUnits),
    };
    return isStated(get(row, ['allowed_quantity_by_construct']))
        ? {...staged, allowed_quantity_by_construct: allowedUnits}
        : staged;
};

/**
 * ...and what figure an emptied box should go back to, in payable units.
 *
 * The rule is one sentence: an emptied box goes back to what the box opened on. Three answers,
 * asked in that order, and it is the same order `committedNonPayableQuantity` fills the box from
 * in the first place - which is what makes clearing an exact undo rather than an approximate one:
 *
 *   the arrival figure    where the row was found payable and carried one. Clearing undoes the
 *                         typing and nothing else.
 *   the construct's own   `constructPayableQuantity` - the cap on a CAPPED line item, nought on a
 *                         DISALLOWED one. This is the answer for the great many rows that arrive
 *                         carrying no figure of their own, the construct's ruling being the only
 *                         thing said about them.
 *   the whole line item   nothing found either way, so nothing is cut.
 *
 * A figure rather than a row, deliberately. What that figure *means* is the caller's to act on,
 * because the answers are two different moves and only one of them is a staging:
 *
 *   fewer than charged   a partial is still a decision, so it is staged like any other.
 *   the charged units    nothing is cut, so the line item is payable and this is a pull-back -
 *                        `revertPcNonPayable`, which lifts the construct's ruling and puts the
 *                        money back. Staging it would leave the row in the Bill Line Item table
 *                        with a ruling still standing against it, and `appliedNonPayableQuantity`
 *                        reads a row with nothing disallowed as one with everything disallowed -
 *                        so an emptied box would come back reading 0 payable, the exact opposite
 *                        of what clearing it asked for.
 *
 * `clearQuantity` is where that choice is made, on the same rule `settleQuantity` uses, so an
 * emptied box and a box settled at the charged units do the same thing - as they must, because
 * they say the same thing.
 */
export const restoredPayableQuantity = (row, baseline) => {
    const charged = chargedQuantity(row);
    const stated = arrivedPayable(baseline) && baseline.money
        ? statedNumber(get(baseline, ['money', 'allowed_units_by_proc_construct']))
        : null;
    if (stated !== null) {
        return clampQuantity(stated, charged);
    }
    const byConstruct = constructPayableQuantity(row);
    return byConstruct === null ? charged : byConstruct;
};

/**
 * Write the decision onto the row, repriced: non-payable by Procedure Construct.
 *
 * This is the commit - every row in the Bill Line Item table goes through it on the way to the
 * save payload - so it is where the row's remark is settled for good. Every other write in this
 * file is reached through the reason dialog, which takes a remark before it lets the change
 * land; this one is reached by pressing Save & Adjudicate, and it commits rows nobody ever
 * opened that dialog on. A line item the construct vetoed and no one touched is decided here,
 * priced here, and would otherwise be sent explaining itself with an empty `remarks`.
 *
 * So the remark is resolved rather than left: a typed one stands, and where there is none the
 * construct's own sentence is written in its place. `resolvedRemarksOf` is the one place that
 * order is decided - see it for why a person's words are never written over.
 */
export const applyPcNonPayable = (row, pcUnit) => {
    const amounts = pcNonPayableAmounts(row, pcUnit);
    const charged = chargedQuantity(row);
    const allowedUnits = Math.max(charged - amounts.pcUnit, 0);
    const verdict = pcVerdictFor(charged, allowedUnits);

    return {
        ...row,
        allowed_units_by_proc_construct: allowedUnits,
        procedure_construct_payable: verdict.payable,
        procedure_construct_payable_text: verdict.text,
        remarks: resolvedRemarksOf(row),
        tariff_amount: amounts.tariffAmount,
        admissible_amount: amounts.tariffAmount,
        savings: amounts.savings,
        deduction: amounts.savings,
        insurerAmount: amounts.insurerAmount,
    };
};

/**
 * Lift the construct's own ruling off a line item.
 *
 * `pcRouteOf` keeps a row in the Bill Line Item table on either of two grounds - the construct
 * ruling against the line item, asked first, or a flagged verdict - so rewriting only the verdict
 * on the way back leaves the first ground standing and the row where it was. Every line item the
 * construct ruled against carries that ruling, so pulling one back would look like it did
 * nothing at all.
 *
 * Both fields the ruling can be carried in are written, because either one left standing is
 * enough to keep the row where it was: `allowed: true` alone would leave a DISALLOWED or CAPPED
 * status ruling, and a status alone would leave a veto. So both are set to the payable answer.
 *
 * A pull-back is a person overruling the construct, and this is what that overruling is: the
 * fields are written to their payable values rather than dropped, so the record still shows the
 * construct ruled on this line item and shows what the ruling was changed to - `allowed_quantity`
 * included, which is deliberately left holding the cap the construct found. `status: PAYABLE` is
 * read before that cap ever is, so the record costs nothing. Rows the construct never ruled on
 * are left exactly as they are - there is nothing to overrule.
 */
const clearConstructFinding = (row) => {
    if (!constructFlagsLineItem(row)) {
        return row;
    }
    return {
        ...row,
        construct_object: {
            ...get(row, ['construct_object'], {}),
            allowed: true,
            status: CONSTRUCT_STATUS.PAYABLE,
        },
    };
};

/**
 * The inverse: pull the line item back to payable.
 *
 * A row that was found payable and disallowed here goes back to the figures it was found with,
 * so `savings` lands on exactly what it arrived as. The verdict is the one exception: it is not
 * restored but rewritten from the units it lands on, because the two have to agree. A row that
 * arrived already disallowed has no payable state to return to, so one is priced from the
 * bill: no units disallowed, the whole line item at tariff.
 *
 * The restored units have a floor, and it is the whole line item. A row found payable that
 * carried no `allowed_units_by_proc_construct` at all - and a great many do, the construct's own
 * ruling being the only thing said about them, which is exactly how a CAPPED row arrives - would
 * otherwise restore that absent field as nought and be rewritten "Non-Payable by Procedure
 * Construct": the precise opposite of what a pull-back is for, and it would leave the row in the
 * table it had just been let out of. Found payable means payable in full, so that is what an
 * unstated figure restores to.
 */
export const revertPcNonPayable = (row, baseline) => {
    if (arrivedPayable(baseline) && baseline.money) {
        const restored = clearConstructFinding({...row, ...baseline.money});
        const charged = chargedQuantity(restored);
        const stated = statedNumber(get(restored, ['allowed_units_by_proc_construct']));
        const allowedUnits = stated === null ? charged : clampQuantity(stated, charged);
        const verdict = pcVerdictFor(charged, allowedUnits);
        return {
            ...restored,
            allowed_units_by_proc_construct: allowedUnits,
            ...(isStated(get(restored, ['allowed_quantity_by_construct']))
                ? {allowed_quantity_by_construct: allowedUnits}
                : {}),
            procedure_construct_payable: verdict.payable,
            procedure_construct_payable_text: verdict.text,
            insurerAmount: insurerAmountFor(restored, allowedUnits),
        };
    }

    const charged = chargedQuantity(row);
    const amounts = pcNonPayableAmounts(row, 0);
    const verdict = pcVerdictFor(charged, charged);
    return {
        ...clearConstructFinding(row),
        ...(isStated(get(row, ['allowed_quantity_by_construct']))
            ? {allowed_quantity_by_construct: charged}
            : {}),
        allowed_units_by_proc_construct: charged,
        procedure_construct_payable: verdict.payable,
        procedure_construct_payable_text: verdict.text,
        tariff_amount: amounts.tariffAmount,
        admissible_amount: amounts.tariffAmount,
        savings: amounts.savings,
        deduction: amounts.savings,
        insurerAmount: amounts.insurerAmount,
    };
};
